import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import CountUp from 'react-countup';
import SweetAlert from 'react-bootstrap-sweetalert';
import Select from 'react-select';
import redirectURL from '../redirectURL';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import MarkerClusterer from '@google/markerclusterer';

/* ─── Design Tokens ─────────────────────────────────────────────── */
const C = {
    bg:        '#F4F6FA',
    surface:   '#FFFFFF',
    accent:    '#0070C0',   // corporate blue (primary UI colour from theme)
    green:     '#00B050',   // positive / delivered (bright green from theme)
    orange:    '#FF5C00',   // warning / FC (vivid orange from theme)
    purple:    '#7C3AED',   // secondary accent (approval rate / billing)
    numColor:  '#1A1A2E',   // near-black for fallback numbers
    border:    '#E2E8F0',
    divider:   '#E2E8F0',
    textPri:   '#1A1A2E',
    textSec:   '#6B7280',
    textMut:   '#9CA3AF',
    chip:      '#EEF2FF',
    chipText:  '#1D4ED8',
    chipActive:'#0070C0',
};

/* ─── Utility ────────────────────────────────────────────────────── */
const safePct = (n, d) =>
    d > 0 ? parseFloat(((n / d) * 100).toFixed(1)) : 0;

/* ─── Style injection ────────────────────────────────────────────── */
const INJECTED_CSS = `
@keyframes foi-spin     { to { transform: rotate(360deg); } }
@keyframes foi-in       { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
@keyframes foi-panel-in { from { transform:translateX(100%); } to { transform:translateX(0); } }
.foi-section { animation: foi-in 0.28s ease both; }
.foi-chip    { transition: background 0.14s, color 0.14s, box-shadow 0.14s; }
.foi-chip:hover:not(:disabled) { opacity: 0.82; }
.foi-stat-cell { transition: background 0.14s; }
.foi-stat-cell:hover { background: #F0F7FF; }
.foi-stat-cell.clickable { cursor: pointer; }
.foi-stat-cell.clickable:hover { background: #E6F2FC; }
.foi-tab-btn { border:none; background:none; cursor:pointer; transition: color 0.14s; outline:none; }
.foi-tab-btn:focus-visible { outline: 2px solid #0070C0; outline-offset: -2px; }
.foi-tip-icon { display:inline-flex; align-items:center; justify-content:center;
    width:15px; height:15px; border-radius:50%;
    border:1.5px solid #94A3B8; color:#94A3B8;
    font-size:9px; font-weight:700; line-height:1;
    cursor:pointer; flex-shrink:0;
    transition: border-color 0.13s, color 0.13s, background 0.13s; }
.foi-tip-icon:hover { border-color:#0070C0; color:#0070C0; background:#EEF6FF; }
`;

/* ─── TooltipIcon ────────────────────────────────────────────────── */
function TooltipIcon({ text }) {
    const [visible, setVisible] = useState(false);
    const [coords,  setCoords]  = useState({ top: 0, left: 0 });
    const iconRef = useRef(null);

    const show = () => {
        if (!iconRef.current) return;
        const r = iconRef.current.getBoundingClientRect();
        setCoords({
            top:  r.top,
            left: r.left + r.width / 2,
        });
        setVisible(true);
    };
    const hide = () => setVisible(false);

    return (
        <>
            <span
                ref={iconRef}
                className="foi-tip-icon"
                onMouseEnter={show}
                onMouseLeave={hide}
                onFocus={show}
                onBlur={hide}
                tabIndex={0}
                role="button"
                aria-label={text}
            >i</span>

            {visible && createPortal(
                <div style={{
                    position:     'fixed',
                    top:          coords.top,
                    left:         coords.left,
                    transform:    'translate(-50%, calc(-100% - 8px))',
                    background:   '#1E293B',
                    color:        '#F8FAFC',
                    padding:      '7px 11px',
                    borderRadius: 7,
                    fontSize:     11,
                    fontWeight:   400,
                    lineHeight:   1.5,
                    maxWidth:     240,
                    zIndex:       99999,
                    pointerEvents:'none',
                    boxShadow:    '0 6px 20px rgba(0,0,0,0.22)',
                    whiteSpace:   'normal',
                }}>
                    {text}
                    <div style={{
                        position:    'absolute',
                        top:         '100%',
                        left:        '50%',
                        transform:   'translateX(-50%)',
                        width:       0, height: 0,
                        borderLeft:  '5px solid transparent',
                        borderRight: '5px solid transparent',
                        borderTop:   '5px solid #1E293B',
                    }} />
                </div>,
                document.body
            )}
        </>
    );
}

/* ─── StatStrip ─────────────────────────────────────────────────── */
/*
 * Matches the reference: one white container, items separated by
 * vertical dividers, no individual card outlines or top-border accents.
 */
function StatStrip({ items, noBorder }) {
    return (
        <div style={{
            overflowX: 'auto',
            ...(noBorder ? {} : {
                borderRadius: 10,
                border: `1px solid ${C.border}`,
            }),
        }}>
        <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${items.length}, minmax(110px, 1fr))`,
            background: C.surface,
            minWidth: `${items.length * 110}px`,
            overflow: 'hidden',
        }}>
            {items.map((item, i) => (
                <div
                    key={i}
                    className={`foi-stat-cell${item.onClick ? ' clickable' : ''}`}
                    onClick={item.onClick || undefined}
                    title={item.onClick ? 'Click to view details' : undefined}
                    style={{
                        padding: '14px 10px 12px',
                        borderRight: i < items.length - 1
                            ? `1px solid ${C.divider}` : 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                        overflow: 'hidden',
                        ...(item.featured ? {
                            background: '#F8FAFF',
                            borderLeft: `3px solid ${C.accent}`,
                            paddingLeft: 7,
                        } : {}),
                    }}
                >
                    {/* Number + icon row */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        flexWrap: 'nowrap',
                        minWidth: 0,
                    }}>
                        <span style={{
                            fontSize: item.featured ? 26 : 22,
                            fontWeight: item.featured ? 900 : 800,
                            color: item.numColor || C.numColor,
                            fontVariantNumeric: 'tabular-nums',
                            lineHeight: 1,
                            letterSpacing: -0.5,
                            flexShrink: 0,
                        }}>
                            <CountUp
                                end={typeof item.value === 'number' ? item.value : 0}
                                decimals={item.isPct ? 1 : 0}
                                suffix={item.isPct ? '%' : ''}
                                separator=","
                                duration={1.1}
                            />
                        </span>
                        {item.tooltip && (
                            <TooltipIcon text={item.tooltip} />
                        )}
                    </div>

                    {/* Badge + Label row */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        marginTop: 6,
                        minWidth: 0,
                        overflow: 'hidden',
                    }}>
                        {item.badge !== undefined && (
                            <span style={{
                                fontSize: 10,
                                fontWeight: 600,
                                color: C.textSec,
                                fontVariantNumeric: 'tabular-nums',
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                            }}>({item.badge}%)</span>
                        )}
                        <span style={{
                            fontSize: 10,
                            fontWeight: item.featured ? 800 : 700,
                            color: item.featured ? C.accent : C.textSec,
                            textTransform: 'uppercase',
                            letterSpacing: 0.6,
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}>{item.label}</span>
                    </div>

                    {/* Sub-label */}
                    {item.sub && (
                        <div style={{
                            fontSize: 9,
                            color: C.textMut,
                            marginTop: 2,
                            lineHeight: 1.3,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}>{item.sub}</div>
                    )}
                </div>
            ))}
        </div>
        </div>
    );
}

/* ─── Proportional Bar ───────────────────────────────────────────── */
function ProportionalBar({ segments, total }) {
    if (!total) return null;
    const COLORS = ['#3B82F6', '#10B981', '#EF4444', '#94A3B8', '#8B5CF6', '#F59E0B'];
    return (
        <div style={{ marginTop: 14 }}>
            <div style={{
                height: 6, borderRadius: 3, overflow: 'hidden',
                display: 'flex', background: C.border,
            }}>
                {segments.map((sg, i) => (
                    <div key={i} style={{
                        width: `${safePct(sg.value, total)}%`,
                        background: sg.color || COLORS[i % COLORS.length],
                        transition: 'width 1s cubic-bezier(.4,0,.2,1)',
                        minWidth: sg.value > 0 ? 2 : 0,
                    }} />
                ))}
            </div>
            <div style={{
                display: 'flex', flexWrap: 'wrap', gap: '4px 18px', marginTop: 7,
            }}>
                {segments.map((sg, i) => (
                    <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        fontSize: 10, color: C.textSec,
                    }}>
                        <div style={{
                            width: 7, height: 7, borderRadius: 1.5,
                            background: sg.color || COLORS[i % COLORS.length],
                            flexShrink: 0,
                        }} />
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {sg.label} — {sg.value.toLocaleString()} ({safePct(sg.value, total)}%)
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ─── Column Chart (vertical bars) ──────────────────────────────── */
const COL_PALETTE = [
    '#60A5FA', '#34D399', '#F87171', '#A78BFA', '#38BDF8',
    '#FBBF24', '#F472B6', '#4ADE80', '#818CF8', '#C084FC',
];

const PIE_COLORS = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
    '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16',
    '#06B6D4', '#A855F7',
];

function ColChart({ data, unit, barColor, multiColor }) {
    if (!data || data.length === 0) {
        return (
            <div style={{ color: C.textMut, fontSize: 11, textAlign: 'center', padding: '18px 0' }}>
                No data available
            </div>
        );
    }
    const max = Math.max(...data.map(d => d.value), 1);
    const BAR_H = 90;

    return (
        <div style={{ paddingTop: 14 }}>
            {/* Bar zone */}
            <div style={{ position: 'relative', height: BAR_H }}>
                {/* Dashed grid lines at 25 / 50 / 75 % */}
                {[75, 50, 25].map(topPct => (
                    <div key={topPct} style={{
                        position: 'absolute', left: 0, right: 0,
                        top: `${topPct}%`,
                        borderTop: '1px dashed #DDE4EF',
                        pointerEvents: 'none',
                    }} />
                ))}
                {/* Columns */}
                <div style={{
                    position: 'absolute', inset: 0,
                    display: 'flex', alignItems: 'flex-end', gap: 3,
                }}>
                    {data.map((d, i) => {
                        const barPx = max > 0 ? (d.value / max) * BAR_H : 0;
                        const labelBottom = barPx + 2;
                        return (
                            <div key={i} style={{
                                flex: 1, position: 'relative', height: '100%',
                                display: 'flex', justifyContent: 'center', alignItems: 'flex-end',
                            }}>
                                <div style={{
                                    position: 'absolute', bottom: labelBottom,
                                    left: 0, right: 0,
                                    fontSize: 8, fontWeight: 600, color: C.textMut,
                                    textAlign: 'center', fontVariantNumeric: 'tabular-nums',
                                    lineHeight: 1, whiteSpace: 'nowrap',
                                }}>
                                    {unit === '%' ? `${d.value.toFixed(1)}%` : d.value.toLocaleString()}
                                </div>
                                <div style={{
                                    width: '72%',
                                    height: Math.max(barPx, d.value > 0 ? 2 : 0),
                                    background: multiColor
                                        ? COL_PALETTE[i % COL_PALETTE.length]
                                        : (barColor || C.accent),
                                    borderRadius: '2px 2px 0 0',
                                }} />
                            </div>
                        );
                    })}
                </div>
            </div>
            {/* X-axis + labels */}
            <div style={{ borderTop: `1.5px solid ${C.border}`, paddingTop: 4, display: 'flex', gap: 3 }}>
                {data.map((d, i) => (
                    <div key={i} style={{
                        flex: 1, fontSize: 8, color: C.textSec,
                        textAlign: 'center', overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }} title={d.fullLabel}>{d.label}</div>
                ))}
            </div>
        </div>
    );
}

/* ─── Chart Card ─────────────────────────────────────────────────── */
function ChartCard({ title, subtitle, children }) {
    return (
        <div style={{
            background: C.surface, borderRadius: 10,
            border: `1px solid ${C.border}`, overflow: 'hidden',
        }}>
            <div style={{
                padding: '10px 16px 9px',
                borderBottom: `1px solid ${C.border}`,
                background: '#F8FAFC',
            }}>
                <div style={{
                    fontSize: 11, fontWeight: 700, color: C.textPri,
                    textTransform: 'uppercase', letterSpacing: 0.6,
                }}>{title}</div>
                {subtitle && (
                    <div style={{ fontSize: 9, color: C.textMut, marginTop: 2, letterSpacing: 0.2 }}>{subtitle}</div>
                )}
            </div>
            <div style={{ padding: '14px 16px 12px' }}>{children}</div>
        </div>
    );
}

/* ─── Pie Chart ──────────────────────────────────────────────────── */
function PieChart({ slices, onSliceClick }) {
    const [hovered, setHovered] = useState(-1);

    const nonZero = (slices || []).filter(s => s.count > 0);
    const total = nonZero.reduce((sum, s) => sum + s.count, 0);

    if (!total) {
        return (
            <div style={{ color: C.textMut, fontSize: 11, textAlign: 'center', padding: '20px 0' }}>
                No data available
            </div>
        );
    }

    const CX = 100, CY = 100, OR = 84, IR = 44;
    const toXY = (a, r) => [CX + r * Math.cos(a), CY + r * Math.sin(a)];

    let angle = -Math.PI / 2;
    const arcs = nonZero.map((s, i) => {
        const theta = (s.count / total) * 2 * Math.PI;
        const sa = angle, ea = angle + theta;
        angle += theta;
        return { ...s, sa, ea, mid: sa + theta / 2, color: PIE_COLORS[i % PIE_COLORS.length] };
    });

    const pathD = (seg) => {
        const span = seg.ea - seg.sa;
        if (span >= 2 * Math.PI - 0.001) {
            const mx = (CX + OR).toFixed(3), hx = (CX - OR).toFixed(3), cy_ = CY.toFixed(3);
            const imx = (CX + IR).toFixed(3), ihx = (CX - IR).toFixed(3);
            return `M${mx} ${cy_} A${OR} ${OR} 0 1 1 ${hx} ${cy_} A${OR} ${OR} 0 1 1 ${mx} ${cy_} L${imx} ${cy_} A${IR} ${IR} 0 1 0 ${ihx} ${cy_} A${IR} ${IR} 0 1 0 ${imx} ${cy_}Z`;
        }
        const [x1, y1] = toXY(seg.sa, OR);
        const [x2, y2] = toXY(seg.ea, OR);
        const [ix1, iy1] = toXY(seg.ea, IR);
        const [ix2, iy2] = toXY(seg.sa, IR);
        const lg = span > Math.PI ? 1 : 0;
        return `M${x1.toFixed(3)} ${y1.toFixed(3)} A${OR} ${OR} 0 ${lg} 1 ${x2.toFixed(3)} ${y2.toFixed(3)} L${ix1.toFixed(3)} ${iy1.toFixed(3)} A${IR} ${IR} 0 ${lg} 0 ${ix2.toFixed(3)} ${iy2.toFixed(3)}Z`;
    };

    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flexShrink: 0 }}>
                <svg width={200} height={200} viewBox="0 0 200 200">
                    {arcs.map((seg, i) => (
                        <path
                            key={i}
                            d={pathD(seg)}
                            fill={seg.color}
                            fillOpacity={hovered === -1 || hovered === i ? 1 : 0.45}
                            stroke="#fff"
                            strokeWidth={2}
                            style={{ cursor: 'pointer', transition: 'fill-opacity 0.15s' }}
                            onClick={() => onSliceClick(seg)}
                            onMouseEnter={() => setHovered(i)}
                            onMouseLeave={() => setHovered(-1)}
                        />
                    ))}
                    <text x={CX} y={CY - 8} textAnchor="middle" fontSize="18" fontWeight="800" fill={C.textPri}>
                        {total.toLocaleString()}
                    </text>
                    <text x={CX} y={CY + 10} textAnchor="middle" fontSize="10" fill={C.textSec} letterSpacing="1.5">
                        TOTAL
                    </text>
                </svg>
            </div>

            <div style={{
                flex: 1, minWidth: 160, display: 'flex', flexDirection: 'column',
                gap: 3, maxHeight: 200, overflowY: 'auto', paddingRight: 4,
            }}>
                {arcs.map((seg, i) => (
                    <div
                        key={i}
                        onClick={() => onSliceClick(seg)}
                        onMouseEnter={() => setHovered(i)}
                        onMouseLeave={() => setHovered(-1)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 7,
                            padding: '3px 6px', borderRadius: 5, cursor: 'pointer',
                            background: hovered === i ? '#EFF6FF' : 'transparent',
                            transition: 'background 0.12s',
                        }}
                    >
                        <div style={{ width: 8, height: 8, borderRadius: 2, background: seg.color, flexShrink: 0 }} />
                        <span style={{
                            fontSize: 10, color: C.textSec, flex: 1,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }} title={seg.label}>{seg.label}</span>
                        <span style={{
                            fontSize: 10, fontWeight: 700, color: C.textPri,
                            fontVariantNumeric: 'tabular-nums', flexShrink: 0, whiteSpace: 'nowrap',
                        }}>{seg.count.toLocaleString()}</span>
                        <span style={{ fontSize: 9, color: C.textMut, flexShrink: 0, whiteSpace: 'nowrap' }}>
                            ({((seg.count / total) * 100).toFixed(1)}%)
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ─── Force Closure drilldown column defs ────────────────────────── */
const FC_DRILL_COL_DEFS = [
    { headerName: 'FO Number',   field: 'fo_number',        minWidth: 140, flex: 1 },
    { headerName: 'Vehicle No.', field: 'truck_no',         minWidth: 120, flex: 1 },
    { headerName: 'Driver No.',  field: 'driver_mobile_no', minWidth: 120, flex: 1 },
    {
        headerName: 'Created Date', field: 'created_date', minWidth: 160, flex: 1.2,
        valueGetter: p => {
            const v = p.data && p.data.created_date;
            if (!v) return '';
            const m = moment.utc(v);
            return m.isValid() ? m.format('YYYY-MM-DD HH:mm:ss') : String(v);
        },
    },
    { headerName: 'Plant Name',  field: 'consigner_name',   minWidth: 180, flex: 1.4 },
];

const FC_TOTAL_DRILL_COL_DEFS = [
    { headerName: 'FO Number',        field: 'fo_number',                      minWidth: 140, flex: 1 },
    { headerName: 'Vehicle No.',      field: 'truck_no',                       minWidth: 120, flex: 1 },
    { headerName: 'Driver No.',       field: 'driver_mobile_no',               minWidth: 120, flex: 1 },
    {
        headerName: 'Created Date', field: 'created_date', minWidth: 160, flex: 1.2,
        valueGetter: p => {
            const v = p.data && p.data.created_date;
            if (!v) return '';
            const m = moment.utc(v);
            return m.isValid() ? m.format('YYYY-MM-DD HH:mm:ss') : String(v);
        },
    },
    { headerName: 'Plant Name',       field: 'consigner_name',                 minWidth: 160, flex: 1.3 },
    { headerName: 'Primary Reason',   field: 'force_closure_reason',           minWidth: 160, flex: 1.4 },
    { headerName: 'Secondary Reason', field: 'force_closure_reason_secondary', minWidth: 200, flex: 1.6 },
];

/* ─── Plant Performance Charts ───────────────────────────────────── */
function PlantCharts({ plantStats, loading }) {
    if (loading) {
        return (
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 8, height: 80, color: C.textSec, fontSize: 11,
            }}>
                <div style={{
                    width: 15, height: 15,
                    border: '2px solid rgba(30,41,59,0.1)',
                    borderTopColor: C.accent, borderRadius: '50%',
                    animation: 'foi-spin 0.7s linear infinite',
                }} />
                Loading plant data…
            </div>
        );
    }
    if (!plantStats || plantStats.length === 0) return null;

    const top10 = (key, desc = true, minTotal = 0) =>
        [...plantStats]
            .filter(p => p.total >= minTotal)
            .sort((a, b) => desc ? b[key] - a[key] : a[key] - b[key])
            .slice(0, 10)
            .map(p => {
                const raw = p.name || p.code;
                return {
                    label: raw.length > 18 ? raw.substring(0, 18) + '…' : raw,
                    fullLabel: `${p.name} (${p.code})`,
                    value: p[key],
                };
            });

    const CHARTS = [
        {
            title: 'TOP 10 PLANTS - CREATED FOs',
            data: top10('total'),
            multiColor: true,
        },
        {
            title: 'TOP 10 PLANTS - ACTIVE FOs',
            data: top10('active'),
            multiColor: true,
        },
        {
            title: 'TOP 10 PLANTS - DELIVERED FOs',
            data: top10('delivered'),
            multiColor: true,
        },
        {
            title: 'TOP 10 PLANTS - HIGHEST DELIVERY %',
            data: top10('deliveryRate', true, 10),
            unit: '%',
            multiColor: true,
        },
        {
            title: 'TOP 10 PLANTS - HIGHEST CONSENT APPROVAL %',
            data: top10('consentApprovedPct', true, 10),
            unit: '%',
            multiColor: true,
        },
        {
            title: 'TOP 10 PLANTS - HIGHEST CONSENT PENDING %',
            data: [...plantStats]
                .filter(p => p.total >= 10)
                .sort((a, b) => a.consentApprovedPct - b.consentApprovedPct)
                .slice(0, 10)
                .map(p => {
                    const raw = p.name || p.code;
                    return {
                        label: raw.length > 18 ? raw.substring(0, 18) + '…' : raw,
                        fullLabel: `${p.name} (${p.code})`,
                        value: parseFloat((100 - p.consentApprovedPct).toFixed(1)),
                    };
                }),
            unit: '%',
            multiColor: true,
        },
        {
            title: 'TOP 10 PLANTS - HIGHEST FORCE CLOSED %',
            data: top10('fcRate', true, 10),
            unit: '%',
            multiColor: true,
        },
        {
            title: 'TOP 10 PLANTS - HIGHEST FORCE CLOSED',
            data: top10('forceClosed', true, 5),
            multiColor: true,
        },
    ];

    return (
        <div style={{ marginTop: 20 }}>
            {/* Section label */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14,
                padding: '0 2px',
            }}>
                <div style={{ width: 9, height: 9, borderRadius: '50%', background: C.accent }} />
                <div style={{
                    fontSize: 13, fontWeight: 800, color: C.textPri,
                    textTransform: 'uppercase', letterSpacing: 0.9,
                }}>Plant Performance Overview</div>
            </div>

            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 14,
            }}>
                {CHARTS.map((ch, i) => (
                    <ChartCard key={i} title={ch.title} subtitle={ch.subtitle}>
                        <ColChart
                            data={ch.data}
                            unit={ch.unit}
                            multiColor={ch.multiColor}
                        />
                    </ChartCard>
                ))}
            </div>
        </div>
    );
}

/* ─── Force Closure Overview Section ────────────────────────────── */
function FCOverviewSection({ data, loading, onSliceClick }) {
    if (loading) {
        return (
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 8, height: 80, color: C.textSec, fontSize: 11,
            }}>
                <div style={{
                    width: 15, height: 15,
                    border: '2px solid rgba(30,41,59,0.1)',
                    borderTopColor: C.orange, borderRadius: '50%',
                    animation: 'foi-spin 0.7s linear infinite',
                }} />
                Loading force closure data…
            </div>
        );
    }
    if (!data) return null;

    const { totalFoCount, totalFoData, primaryFo, secondaryFo } = data;

    return (
        <div style={{ marginTop: 20 }}>
            {/* Section header + FO Level chip */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                marginBottom: 14, padding: '0 2px', flexWrap: 'wrap',
            }}>
                <div style={{ width: 9, height: 9, borderRadius: '50%', background: C.orange, flexShrink: 0 }} />
                <div style={{
                    fontSize: 13, fontWeight: 800, color: C.textPri,
                    textTransform: 'uppercase', letterSpacing: 0.9, flexShrink: 0,
                }}>Force Closure Overview</div>

                <div style={{ flex: 1 }} />

                <button
                    onClick={() => onSliceClick('Total Force Closed FOs', totalFoData, FC_TOTAL_DRILL_COL_DEFS)}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '3px 12px', borderRadius: 20,
                        background: '#FFF3E0', border: '1px solid #FFCC80',
                        color: C.orange, fontSize: 11, fontWeight: 700,
                        cursor: 'pointer', fontVariantNumeric: 'tabular-nums',
                        flexShrink: 0, outline: 'none',
                    }}
                    title="Click to view all force closed FO records"
                >
                    FO Level:&nbsp;<span style={{ fontWeight: 800 }}>{totalFoCount.toLocaleString()}</span>
                </button>
            </div>

            {/* Primary & Secondary Reasons side by side */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <ChartCard title="TOTAL PRIMARY REASONS — FO LEVEL">
                    <PieChart
                        slices={primaryFo}
                        onSliceClick={seg => onSliceClick(`${seg.label} — FO Level`, seg.data)}
                    />
                </ChartCard>
                <ChartCard title="TOTAL SECONDARY REASONS — FO LEVEL">
                    <PieChart
                        slices={secondaryFo}
                        onSliceClick={seg => onSliceClick(`${seg.label} — FO Level`, seg.data)}
                    />
                </ChartCard>
            </div>
        </div>
    );
}

/* ─── Section Card ───────────────────────────────────────────────── */
function SectionCard({ title, accentColor, children, loading, barSegments, barTotal, style: styleProp }) {
    return (
        <div className="foi-section" style={{
            background: C.surface,
            borderRadius: 12,
            border: `1px solid ${C.border}`,
            overflow: 'hidden',
            marginBottom: 16,
            opacity: loading ? 0.6 : 1,
            transition: 'opacity 0.25s',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            ...styleProp,
        }}>
            {/* Header */}
            <div style={{
                padding: '13px 20px 12px',
                borderBottom: `1px solid ${C.border}`,
                display: 'flex', alignItems: 'center', gap: 8,
            }}>
                <div style={{
                    width: 9, height: 9, borderRadius: '50%',
                    background: accentColor, flexShrink: 0,
                }} />
                <div style={{
                    fontSize: 13, fontWeight: 800, color: C.textPri,
                    textTransform: 'uppercase', letterSpacing: 0.9,
                }}>{title}</div>
            </div>

            {/* Body */}
            <div style={{ padding: '16px 20px 14px' }}>
                {children}
            </div>

            {/* Bottom proportional bar */}
            {barSegments && barTotal > 0 && (
                <div style={{ height: 5, display: 'flex', background: C.border }}>
                    {barSegments.map((seg, i) => (
                        <div key={i} style={{
                            width: `${safePct(seg.value, barTotal)}%`,
                            background: seg.color,
                            transition: 'width 1s cubic-bezier(.4,0,.2,1)',
                            minWidth: seg.value > 0 ? 2 : 0,
                        }} />
                    ))}
                </div>
            )}
        </div>
    );
}

/* ─── Sub-group inside Section 3 ────────────────────────────────── */
function SubGroup({ title, children }) {
    return (
        <div style={{
            background: C.surface,
            borderRadius: 8,
            border: `1px solid ${C.border}`,
            overflow: 'hidden',
        }}>
            <div style={{
                padding: '9px 16px 8px',
                borderBottom: `1px solid ${C.border}`,
                fontSize: 10, fontWeight: 700,
                color: C.textSec,
                textTransform: 'uppercase',
                letterSpacing: 1.1,
                background: '#F8FAFC',
            }}>{title}</div>
            <div style={{ padding: '0' }}>
                {children}
            </div>
        </div>
    );
}

/* ─── Insight Strip ──────────────────────────────────────────────── */
function InsightStrip({ items }) {
    if (!items.length) return null;
    return (
        <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '6px 24px',
            padding: '10px 14px',
            background: C.bg,
            borderRadius: 8,
            marginTop: 14,
            border: `1px solid ${C.border}`,
        }}>
            {items.map((it, i) => (
                <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    fontSize: 11, color: C.textSec,
                }}>
                    <div style={{
                        width: 6, height: 6, borderRadius: '50%',
                        background: it.good ? C.green : C.amber, flexShrink: 0,
                    }} />
                    <span>{it.label}:</span>
                    <span style={{
                        fontWeight: 800,
                        color: it.good ? C.green : C.amber,
                        fontVariantNumeric: 'tabular-nums',
                    }}>{it.value}</span>
                </div>
            ))}
        </div>
    );
}

/* ─── Date range chip label ──────────────────────────────────────── */
function DateRangeChip({ startDate, endDate }) {
    if (!startDate && !endDate) return null;
    return (
        <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 11, color: C.textSec, fontWeight: 600,
            background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: 6, padding: '4px 12px',
        }}>
            <span style={{
                width: 6, height: 6, borderRadius: '50%',
                background: C.green, display: 'inline-block',
            }} />
            {moment(startDate).format('DD MMM YYYY')} – {moment(endDate).format('DD MMM YYYY')}
        </div>
    );
}

/* ─── Drilldown AG-Grid helpers ──────────────────────────────────── */
const STATUS_CHIP = s => {
    if (['Inside Plant', 'In Transit', 'Near Customer'].includes(s)) return { bg: '#DBEAFE', color: '#1D4ED8' };
    if (['Reached Customer', 'Left Customer'].includes(s))           return { bg: '#D1FAE5', color: '#059669' };
    if (s === 'Force Closed')  return { bg: '#FEE2E2', color: '#DC2626' };
    if (s === 'Cancelled')     return { bg: '#F1F5F9', color: '#64748B' };
    return { bg: '#F1F5F9', color: '#64748B' };
};

const DrillStatusRenderer = params => {
    const sc = STATUS_CHIP(params.value);
    return (
        <span style={{
            background: sc.bg, color: sc.color,
            padding: '2px 8px', borderRadius: 4,
            fontWeight: 700, fontSize: 10, whiteSpace: 'nowrap',
        }}>
            {params.value || '—'}
        </span>
    );
};

const fmtDrillDate = v => {
    if (!v) return '—';
    try { const m = moment.utc(v); return m.isValid() ? m.format('YYYY-MM-DD HH:mm:ss') : String(v); } catch { return '—'; }
};

const DRILL_COL_DEFS = [
    { headerName: 'FO Number',      field: 'fo_number',          minWidth: 140, flex: 1 },
    { headerName: 'Plant Name',     field: 'plant_name',         minWidth: 160, flex: 1.2 },
    { headerName: 'Customer Name',  field: 'customer_name',      minWidth: 160, flex: 1.2 },
    { headerName: 'Truck No.',      field: 'truck_no',           minWidth: 110, flex: 1 },
    { headerName: 'Driver Number',  field: 'driver_mobile_no',   minWidth: 130, flex: 1 },
    { headerName: 'Driver Name',    field: 'driver_name',        minWidth: 140, flex: 1 },
    { headerName: 'Status',         field: 'status',             minWidth: 160, flex: 1.2, cellRenderer: DrillStatusRenderer },
    { headerName: 'Consent Status', field: 'sim_consent_status', minWidth: 140, flex: 1 },
    { headerName: 'Created Date',   field: 'created_date',       minWidth: 130, flex: 1, valueGetter: p => fmtDrillDate(p.data && p.data.created_date) },
    { headerName: 'Tracking Mode',  field: 'tracking_mode',      minWidth: 130, flex: 1 },
];

const DRILL_DEFAULT_COL = { sortable: true, resizable: true, filter: true };

function DrilldownPanel({ visible, title, loading, data, onClose, columnDefs: colDefsOverride }) {
    const colDefs = colDefsOverride || DRILL_COL_DEFS;
    const gridRef = useRef(null);

    const exportCSV = () => {
        if (gridRef.current?.api) {
            gridRef.current.api.exportDataAsCsv({
                fileName: `${title.replace(/\s+/g, '_')}_${moment().format('YYYYMMDD_HHmm')}.csv`,
            });
        }
    };

    if (!visible) return null;

    return createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 9998, display: 'flex', justifyContent: 'flex-end' }}>
            {/* Backdrop */}
            <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(15,29,53,0.45)' }} />

            {/* Panel */}
            <div style={{
                position: 'relative',
                width: 'min(1000px, 80vw)',
                height: '100vh',
                background: '#FFFFFF',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '-8px 0 48px rgba(0,0,0,0.18)',
                animation: 'foi-panel-in 0.22s cubic-bezier(.4,0,.2,1) both',
            }}>
                {/* Header */}
                <div style={{
                    padding: '14px 24px',
                    borderBottom: `1px solid ${C.border}`,
                    display: 'flex', alignItems: 'center', gap: 14,
                    flexShrink: 0, background: '#F8FAFF',
                }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: C.textPri }}>{title}</div>
                        <div style={{ fontSize: 11, color: C.textSec, marginTop: 2 }}>
                            {loading ? 'Loading…' : `${data.length.toLocaleString()} records`}
                        </div>
                    </div>

                    {!loading && data.length > 0 && (
                        <button onClick={exportCSV} style={{
                            padding: '7px 14px', borderRadius: 7,
                            border: `1px solid ${C.border}`, background: C.surface,
                            color: C.chipText, fontSize: 11, fontWeight: 700,
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
                        }}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                                <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
                            </svg>
                            Export CSV
                        </button>
                    )}

                    <button onClick={onClose} style={{
                        width: 32, height: 32, borderRadius: 8,
                        border: `1px solid ${C.border}`, background: C.surface,
                        color: C.textSec, fontSize: 16, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>✕</button>
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflow: 'hidden' }}>
                    {loading ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220, gap: 10, color: C.textSec, fontSize: 12 }}>
                            <div style={{
                                width: 20, height: 20,
                                border: '3px solid rgba(30,41,59,0.1)',
                                borderTopColor: C.chipActive,
                                borderRadius: '50%',
                                animation: 'foi-spin 0.7s linear infinite',
                            }} />
                            Loading data…
                        </div>
                    ) : data.length === 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220, color: C.textMut, fontSize: 13 }}>
                            No records found for this filter
                        </div>
                    ) : (
                        <div className="ag-theme-alpine" style={{ height: '100%', width: '100%' }}>
                            <AgGridReact
                                ref={gridRef}
                                rowData={data}
                                columnDefs={colDefs}
                                defaultColDef={DRILL_DEFAULT_COL}
                                rowHeight={40}
                                headerHeight={38}
                                suppressRowClickSelection={true}
                                animateRows={true}
                                pagination={true}
                                paginationPageSize={100}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}

/* ─── FO Tracking Tab ───────────────────────────────────────────── */
const FT_COL_DEFS = [
    {
        headerName: 'Plant Name', field: 'consigner_name', width: 160, pinned: 'left',
        cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5', cursor: 'pointer' } : null,
    },
    {
        headerName: 'Plant Code', field: 'consigner_code', width: 110, pinned: 'left',
        cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5', cursor: 'pointer' } : null,
    },
    {
        headerName: 'FO Details',
        children: [
            { headerName: 'FO Created (Enmovil)', field: 'fo_created', width: 140,
              valueGetter: p => p?.data?.fo_created?.count ?? 0,
              cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5' } : null },
            { headerName: 'Delay in FO Creation', field: 'delayed_fo_created', width: 140,
              valueGetter: p => p?.data?.delayed_fo_created?.count ?? 0,
              cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5' } : null },
        ],
    },
    {
        headerName: 'Tracking Mode',
        children: [
            { headerName: 'GPS Tracked', field: 'gps_mode', width: 115,
              valueGetter: p => p?.data?.gps_mode?.count ?? 0,
              cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5' } : null },
            { headerName: 'SIM Tracked', field: 'sim_mode', width: 115,
              valueGetter: p => p?.data?.sim_mode?.count ?? 0,
              cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5' } : null },
        ],
    },
    {
        headerName: 'Consent Status',
        children: [
            { headerName: 'Consent Approved', field: 'consent_approved', width: 130,
              valueGetter: p => p?.data?.consent_approved?.count ?? 0,
              cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5' } : null },
            { headerName: 'Consent Pending', field: 'consent_pending', width: 125,
              valueGetter: p => p?.data?.consent_pending?.count ?? 0,
              cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5' } : null },
            { headerName: 'BSNL No.', field: 'not_registered_sim_or_bsnl', width: 105,
              valueGetter: p => p?.data?.not_registered_sim_or_bsnl?.count ?? 0,
              cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5' } : null },
            { headerName: 'Invalid Mobile No.', field: 'invalid_mobile_no', width: 135,
              valueGetter: p => p?.data?.invalid_mobile_no?.count ?? 0,
              cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5' } : null },
            { headerName: 'Consent Success %', field: 'consent_success_percent', width: 130,
              valueGetter: p => (p?.data?.consent_success_percent ?? 0) + '%',
              cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5' } : null },
        ],
    },
    {
        headerName: 'App Usage',
        children: [
            { headerName: 'Gate In', field: 'manual_gate_in_given', width: 95,
              valueGetter: p => p?.data?.manual_gate_in_given?.count ?? 0,
              cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5' } : null },
            { headerName: 'Pending Gate In', field: 'manual_gate_in_not_given', width: 125,
              valueGetter: p => p?.data?.manual_gate_in_not_given?.count ?? 0,
              cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5' } : null },
            { headerName: 'Gate Out', field: 'manual_gate_out_given', width: 100,
              valueGetter: p => p?.data?.manual_gate_out_given?.count ?? 0,
              cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5' } : null },
            { headerName: 'Pending Gate Out', field: 'manual_gate_out_not_given', width: 125,
              valueGetter: p => p?.data?.manual_gate_out_not_given?.count ?? 0,
              cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5' } : null },
            { headerName: 'Mobile App Usage %', field: 'mobile_app_usage_percent', width: 140,
              valueGetter: p => (p?.data?.mobile_app_usage_percent ?? 0) + '%',
              cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5' } : null },
        ],
    },
    {
        headerName: 'Hygiene Checklist',
        children: [
            { headerName: 'Approved', field: 'hygiene_approved', width: 100,
              valueGetter: p => p?.data?.hygiene_approved?.count ?? (p?.data?.hygiene_approved ?? 0),
              cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5' } : null },
            { headerName: 'Draft', field: 'hygiene_draft', width: 90,
              valueGetter: p => p?.data?.hygiene_draft?.count ?? (p?.data?.hygiene_draft ?? 0),
              cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5' } : null },
            { headerName: 'Hygiene Checklist %', field: 'hygiene_checklist_percent', width: 145,
              valueGetter: p => (p?.data?.hygiene_checklist_percent ?? 0) + '%',
              cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5' } : null },
        ],
    },
    {
        headerName: 'Inbound Checklist %', field: 'inbound_checklist_percent', width: 150,
        valueGetter: p => (p?.data?.inbound_checklist_percent ?? 0) + '%',
        cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5', cursor: 'pointer' } : null,
    },
    {
        headerName: 'OTR Raised', field: 'otr_raised', width: 110,
        valueGetter: p => p?.data?.otr_raised?.count ?? (p?.data?.otr_raised ?? 0),
        cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5', cursor: 'pointer' } : null,
    },
    {
        headerName: 'Trip Closure',
        children: [
            { headerName: 'Geofence Closed', field: 'geofence_closed', width: 130,
              valueGetter: p => p?.data?.geofence_closed?.count ?? 0,
              cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5' } : null },
            { headerName: 'Force Close', field: 'manual_closed', width: 110,
              valueGetter: p => p?.data?.manual_closed?.count ?? 0,
              cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5' } : null },
            { headerName: 'Intransit', field: 'intransit', width: 100,
              valueGetter: p => p?.data?.intransit?.count ?? 0,
              cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5' } : null },
            { headerName: 'e-POD Submitted', field: 'epod', width: 125,
              valueGetter: p => p?.data?.epod?.count ?? 0,
              cellStyle: p => p.node.rowPinned ? { fontWeight: 700, background: '#F0F2F5' } : null },
        ],
    },
];

const FT_STATUS_LABELS = {
    1: 'In Transit', 2: 'In Transit', 3: 'Delivered',
    4: 'Delivered',  5: 'Delivered',  6: 'Force Closed',
    8: 'Cancelled',  9: 'Force Closed',
};
const fmtFTDate = v => {
    if (!v) return '';
    const m = moment.utc(v);
    return m.isValid() ? m.format('YYYY-MM-DD HH:mm:ss') : String(v);
};

const FT_DETAIL_COL_DEFS = [
    { headerName: 'FO Number',       field: 'fo_number',        flex: 1,   minWidth: 130 },
    { headerName: 'Vehicle No.',     field: 'truck_no',         flex: 1,   minWidth: 110 },
    { headerName: 'Driver No.',      field: 'driver_mobile_no', flex: 1,   minWidth: 120 },
    { headerName: 'Created Date',    field: 'created_date',     flex: 1.2, minWidth: 160,
      valueGetter: p => fmtFTDate(p.data?.created_date) },
    { headerName: 'Plant Name',      field: 'consigner_name',   flex: 1.3, minWidth: 160 },
    { headerName: 'Status',          field: 'status',           flex: 1,   minWidth: 130,
      valueGetter: p => {
          const s = p.data?.status;
          return FT_STATUS_LABELS[s] ?? (s !== undefined && s !== '' ? String(s) : '');
      } },
    { headerName: 'Manual Gate In',  field: 'manual_gate_in',   flex: 1.2, minWidth: 160,
      valueGetter: p => fmtFTDate(p.data?.manual_gate_in) },
    { headerName: 'Manual Gate Out', field: 'manual_gate_out',  flex: 1.2, minWidth: 160,
      valueGetter: p => fmtFTDate(p.data?.manual_gate_out) },
];

const FT_CSV_COLS = [
    { header: 'FO Number',       key: 'fo_number' },
    { header: 'Vehicle No.',     key: 'truck_no' },
    { header: 'Driver No.',      key: 'driver_mobile_no' },
    { header: 'Created Date',    key: 'created_date',    isDate: true },
    { header: 'Plant Name',      key: 'consigner_name' },
    { header: 'Status',          key: 'status',          isStatus: true },
    { header: 'Manual Gate In',  key: 'manual_gate_in',  isDate: true },
    { header: 'Manual Gate Out', key: 'manual_gate_out', isDate: true },
];

function downloadFTCsv(rows, label) {
    if (!Array.isArray(rows) || rows.length === 0) return;
    const csvRows = [FT_CSV_COLS.map(c => c.header).join(',')];
    rows.forEach(row => {
        csvRows.push(FT_CSV_COLS.map(c => {
            let v = row[c.key] ?? '';
            if (c.isDate && v)   v = fmtFTDate(v);
            if (c.isStatus)      v = FT_STATUS_LABELS[v] ?? (v !== '' ? String(v) : '');
            if (typeof v === 'string') v = v.replace(/"/g, '""');
            return `"${v}"`;
        }).join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${(label || 'FO_Tracking').replace(/[^a-z0-9]/gi, '_')}_${moment().format('YYYYMMDD_HHmm')}.csv`;
    link.click();
}

function FOTrackingSection({ data, loading, totalData }) {
    const gridRef = useRef(null);
    const [detailParams, setDetailParams] = useState(null);

    /* Re-size columns whenever data changes (filter/date change), but NOT on row expand */
    useEffect(() => {
        if (gridRef.current?.api) {
            gridRef.current.api.autoSizeAllColumns();
        }
    }, [data]);

    const pinnedRow = totalData
        ? [{ consigner_name: 'TOTAL', ...totalData, isTotal: true }]
        : [];

    const fetchTrackingFields = async (foNumbers) => {
        try {
            const resp = await redirectURL.post('/dashboard/getFOTrackingFields', { fo_numbers: foNumbers });
            return resp.data?.data || {};
        } catch (e) {
            return {};
        }
    };

    const onCellClicked = async (params) => {
        const cellVal = params.data?.[params.colDef.field];
        const hasData = cellVal && typeof cellVal === 'object' && Array.isArray(cellVal.data) && cellVal.data.length > 0;

        /* TOTAL row → download CSV with enriched fields from consignments */
        if (params.node.rowPinned === 'bottom') {
            if (hasData) {
                const foNumbers = cellVal.data.map(r => r.fo_number).filter(Boolean);
                const extraFields = await fetchTrackingFields(foNumbers);
                const enriched = cellVal.data.map(r => ({ ...r, ...(extraFields[r.fo_number] || {}) }));
                downloadFTCsv(enriched, params.colDef.headerName || params.colDef.field);
            }
            return;
        }

        /* Regular row → master-detail expand, injecting plant name + status/gate fields */
        if (hasData) {
            const isExpanded = params.node.expanded;
            if (!isExpanded) {
                const plantName = params.data?.consigner_name || '';
                const foNumbers = cellVal.data.map(r => r.fo_number).filter(Boolean);
                const extraFields = await fetchTrackingFields(foNumbers);
                setDetailParams({
                    detailGridOptions: {
                        columnDefs: FT_DETAIL_COL_DEFS,
                        defaultColDef: { flex: 1, sortable: true, filter: true, resizable: true },
                    },
                    getDetailRowData: (p2) => p2.successCallback(
                        cellVal.data.map(r => ({
                            ...r,
                            ...(extraFields[r.fo_number] || {}),
                            consigner_name: plantName,
                        }))
                    ),
                });
            }
            params.node.setExpanded(!isExpanded);
        }
    };

    return (
        <div className="foi-section" style={{
            background: C.surface,
            borderRadius: 12,
            border: `1px solid ${C.border}`,
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}>
            {/* Header */}
            <div style={{
                padding: '13px 20px 12px',
                borderBottom: `1px solid ${C.border}`,
                display: 'flex', alignItems: 'center', gap: 8,
                background: '#F8FAFC',
            }}>
                <div style={{
                    width: 9, height: 9, borderRadius: '50%',
                    background: C.accent, flexShrink: 0,
                }} />
                <div style={{
                    fontSize: 13, fontWeight: 800, color: C.textPri,
                    textTransform: 'uppercase', letterSpacing: 0.9,
                }}>FO Tracking</div>
            </div>

            {/* Grid */}
            <div style={{ padding: '16px 20px 14px' }}>
                {loading && data.length === 0 ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 80, color: C.textSec, fontSize: 11 }}>
                        <div style={{ width: 15, height: 15, border: '2px solid rgba(30,41,59,0.1)', borderTopColor: C.accent, borderRadius: '50%', animation: 'foi-spin 0.7s linear infinite' }} />
                        Loading…
                    </div>
                ) : (
                    <div className="ag-theme-alpine" style={{ height: 500, width: '100%' }}>
                        <AgGridReact
                            ref={gridRef}
                            columnDefs={FT_COL_DEFS}
                            rowData={data}
                            loading={loading}
                            defaultColDef={{ sortable: true, resizable: true, filter: true }}
                            groupHeaderHeight={50}
                            headerHeight={50}
                            rowHeight={38}
                            masterDetail={true}
                            detailCellRendererParams={detailParams}
                            pinnedBottomRowData={pinnedRow}
                            onCellClicked={onCellClicked}
                            suppressColumnVirtualisation={true}
                            onFirstDataRendered={p => p.api.autoSizeAllColumns()}
                            pagination={true}
                            paginationPageSize={50}
                            enableRangeSelection={true}
                            sideBar={{
                                toolPanels: [
                                    { id: 'columns', labelDefault: 'Columns', labelKey: 'columns', iconKey: 'columns', toolPanel: 'agColumnsToolPanel',
                                      toolPanelParams: { suppressPivotMode: true, suppressRowGroups: true, suppressValues: true } },
                                    { id: 'filters', labelDefault: 'Filters', labelKey: 'filters', iconKey: 'filter', toolPanel: 'agFiltersToolPanel' },
                                ],
                            }}
                            statusBar={{
                                statusPanels: [
                                    { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' },
                                    { statusPanel: 'agTotalRowCountComponent', align: 'center' },
                                    { statusPanel: 'agFilteredRowCountComponent' },
                                    { statusPanel: 'agSelectedRowCountComponent' },
                                    { statusPanel: 'agAggregationComponent' },
                                ],
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

/* ─── Presets ────────────────────────────────────────────────────── */
const PRESETS = [
    { id: 'l2',  label: 'Last 2 Days',  days: 2 },
    { id: 'l7',  label: 'Last 7 Days',  days: 7 },
    { id: 'l15', label: 'Last 15 Days', days: 15 },
    { id: 'lm',  label: 'Last Month',   days: 30 },
];

const EMPTY = {
    section1: { total: 0, active: 0, delivered: 0, forceClosed: 0, cancelled: 0 },
    section2: { total: 0, approved: 0, pending: 0 },
    section3: { total: 0, gps: 0, sim: 0, invalid: 0, bsnl: 0 },
    plantStats: [],
};

/* ─── Truck Visibility Tab ───────────────────────────────────────── */
const TV_COLORS = {
    moving:         '#22C55E',
    idle:           '#F59E0B',
    delayed:        '#EF4444',
    at_destination: '#3B82F6',
};
const TV_LABELS = {
    moving:         'Moving',
    idle:           'Idle (1–3h)',
    delayed:        'Delayed (3h+)',
    at_destination: 'At Destination',
};
const TV_FO_STATUS = {
    1: 'Inside Plant', 2: 'In Transit', 3: 'Near Customer',
    4: 'Reached Customer', 5: 'Left Customer', 6: 'Force Closed', 8: 'Cancelled',
};
const TV_DESCRIPTIONS = {
    all:            'All trucks with live GPS data in the selected date & plant filters',
    moving:         'Trucks that have moved within the last 1 hour',
    idle:           'Trucks with no movement for 1 to 3 hours',
    delayed:        'Active FO trucks that have been stationary for more than 3 hours',
    at_destination: 'FOs at Near Customer, Reached Customer, Left Customer',
};
const TV_STATUSES = ['moving', 'idle', 'delayed'];
const INDIA_CENTER = { lat: 22.5937, lng: 80.9629 };

const TV_GRID_COL_DEFS = [
    { headerName: 'FO Number',       field: 'fo_number',        flex: 1.2, minWidth: 140, sortable: true, filter: true, resizable: true },
    { headerName: 'Truck No.',        field: 'truck_no',         flex: 1,   minWidth: 120, sortable: true, filter: true, resizable: true },
    {
        headerName: 'Status', field: 'status', flex: 1, minWidth: 140, sortable: true, filter: true, resizable: true,
        valueGetter: p => TV_LABELS[p.data?.status] || p.data?.status || '',
        cellStyle: p => {
            const color = TV_COLORS[p.data?.status];
            return color ? { color, fontWeight: 700 } : {};
        },
    },
    { headerName: 'Last Packet Time',  field: 'last_packet_time', flex: 1.3, minWidth: 180, sortable: true, filter: true, resizable: true },
    { headerName: 'Transporter Name',  field: 'transporter_name', flex: 1.3, minWidth: 160, sortable: true, filter: true, resizable: true },
    { headerName: 'Tracking Mode',     field: 'tracking_mode',    flex: 0.9, minWidth: 130, sortable: true, filter: true, resizable: true },
    { headerName: 'Driver Name',       field: 'driver_name',      flex: 1.2, minWidth: 150, sortable: true, filter: true, resizable: true },
    { headerName: 'Driver Mobile No',  field: 'driver_mobile_no', flex: 1.1, minWidth: 145, sortable: true, filter: true, resizable: true },
];

const TV_GMAP_KEY = 'AIzaSyCbVLHDKmRETk7xp5RZzUdMYPCZe7Is59w';

const TV_STATUS_PRIORITY = { delayed: 3, idle: 2, moving: 1, at_destination: 0 };

const _mkClusterStyle = (color, size) => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">` +
        `<circle cx="${size/2}" cy="${size/2}" r="${size/2 - 1}" fill="${color}" opacity="0.92" stroke="white" stroke-width="2.5"/>` +
        `</svg>`;
    return { url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`, width: size, height: size, textColor: '#fff', textSize: 12 };
};
// Styles indexed 1-based: 1=delayed(red), 2=idle(amber), 3=moving(green), 4=at_destination(blue)
const TV_CLUSTER_STYLES = [
    _mkClusterStyle(TV_COLORS.delayed,        46),
    _mkClusterStyle(TV_COLORS.idle,           42),
    _mkClusterStyle(TV_COLORS.moving,         42),
    _mkClusterStyle(TV_COLORS.at_destination, 42),
];
const TV_CLUSTER_CALC = (markers) => {
    const dominant = markers
        .map(m => m.get('tvStatus') || 'moving')
        .reduce((best, s) => ((TV_STATUS_PRIORITY[s] ?? -1) > (TV_STATUS_PRIORITY[best] ?? -1) ? s : best), 'moving');
    return { text: String(markers.length), index: { delayed: 1, idle: 2, moving: 3, at_destination: 4 }[dominant] || 3 };
};

function TruckVisibilitySection({ startDate, endDate, selectedPlant, selectedZone }) {
    const [trucks,       setTrucks]       = useState([]);
    const [tvLoading,    setTvLoading]    = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [hoveredTip,   setHoveredTip]   = useState(null);
    const [mapReady,     setMapReady]     = useState(false);

    const mapDivRef     = useRef(null);
    const mapRef        = useRef(null);
    const markersRef    = useRef([]);
    const infoWinRef    = useRef(null);
    const clustererRef  = useRef(null);

    const initMapInstance = useCallback(() => {
        if (!mapDivRef.current || mapRef.current) return;
        try {
            mapRef.current = new window.google.maps.Map(mapDivRef.current, {
                zoom: 5,
                center: { lat: 22.5937, lng: 80.9629 },
                mapTypeId: window.google.maps.MapTypeId.ROADMAP,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: true,
            });
            infoWinRef.current = new window.google.maps.InfoWindow();
            setMapReady(true);
        } catch (e) {
            console.error('TV Map init error:', e);
        }
    }, []);

    useEffect(() => {
        if (window.google && window.google.maps) {
            initMapInstance();
        } else {
            window.initTVMap = initMapInstance;
            if (!document.querySelector('script[data-tv-gmap]')) {
                const s = document.createElement('script');
                s.src = `https://maps.googleapis.com/maps/api/js?key=${TV_GMAP_KEY}&libraries=places&callback=initTVMap`;
                s.async = true;
                s.defer = true;
                s.setAttribute('data-tv-gmap', '1');
                document.head.appendChild(s);
            }
        }
        return () => { delete window.initTVMap; };
    }, [initMapInstance]);

    const fetchTrucks = useCallback(async () => {
        setTvLoading(true);
        try {
            const body = {
                startDate: moment(startDate).format('YYYY-MM-DD'),
                endDate:   moment(endDate).format('YYYY-MM-DD'),
            };
            if (selectedPlant && selectedPlant !== 'ALL') body.consignerCode = selectedPlant;
            if (selectedZone  && selectedZone  !== 'ALL') body.zone           = selectedZone;
            const resp = await redirectURL.post('/dashboard/getFOTruckVisibility', body);
            setTrucks(resp.data?.data || []);
        } catch (e) {
            console.error('TruckVisibility:', e);
        } finally {
            setTvLoading(false);
        }
    }, [startDate, endDate, selectedPlant, selectedZone]);

    useEffect(() => { fetchTrucks(); }, [fetchTrucks]);

    const visible = statusFilter === 'all' ? trucks : trucks.filter(t => t.status === statusFilter);
    const counts  = TV_STATUSES.reduce((a, s) => { a[s] = trucks.filter(t => t.status === s).length; return a; }, {});

    // One pin per physical truck — pick most-urgent FO status for marker color
    const truckMarkerMap = new Map();
    visible.forEach(fo => {
        const existing = truckMarkerMap.get(fo.truck_no);
        if (!existing || (TV_STATUS_PRIORITY[fo.status] ?? -1) > (TV_STATUS_PRIORITY[existing.status] ?? -1)) {
            truckMarkerMap.set(fo.truck_no, fo);
        }
    });
    const truckMarkers = Array.from(truckMarkerMap.values());

    const mkIcon = (status) => {
        const color = TV_COLORS[status] || '#94A3B8';
        const svg =
            `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">` +
            `<path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 26 14 26S28 24.5 28 14C28 6.268 21.732 0 14 0z" fill="${color}" stroke="white" stroke-width="1.8"/>` +
            `<circle cx="14" cy="14" r="6" fill="white" opacity="0.9"/>` +
            `</svg>`;
        return {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
            scaledSize: new window.google.maps.Size(28, 40),
            anchor:     new window.google.maps.Point(14, 40),
        };
    };

    useEffect(() => {
        if (!mapReady || !mapRef.current || !window.google) return;

        // Clear old markers and clusterer
        if (clustererRef.current) {
            clustererRef.current.clearMarkers();
        }
        markersRef.current = [];
        if (infoWinRef.current) infoWinRef.current.close();

        const newMarkers = [];
        truckMarkers.forEach(t => {
            if (!t.lat || !t.lng) return;
            const marker = new window.google.maps.Marker({
                position: { lat: t.lat, lng: t.lng },
                title:    t.truck_no,
                icon:     mkIcon(t.status),
            });
            marker.set('tvStatus', t.status);

            const statusColor = TV_COLORS[t.status] || '#374151';
            const row = (label, value, valueStyle = '') =>
                `<tr style="border-bottom:1px solid #f3f4f6">` +
                `<td style="padding:7px 14px;font-weight:600;color:#6b7280;white-space:nowrap;font-size:12px">${label}</td>` +
                `<td style="padding:7px 14px;color:#111827;font-size:12px;${valueStyle}">${value}</td>` +
                `</tr>`;
            const content =
                `<div style="font-family:Arial,sans-serif;min-width:270px;max-width:340px;border-radius:8px;overflow:hidden">` +
                `<div style="display:flex;align-items:center;gap:8px;padding:10px 14px;background:#f9fafb;border-bottom:2px solid ${statusColor}">` +
                `<span style="font-size:18px">🚛</span>` +
                `<span style="font-weight:700;font-size:14px;color:#111827">${t.truck_no}</span>` +
                `</div>` +
                `<table style="width:100%;border-collapse:collapse">` +
                (t.fo_number        ? row('FO Number',        t.fo_number) : '') +
                (t.plant_name       ? row('Plant',            t.plant_name) : '') +
                (t.zone             ? row('Zone',             t.zone) : '') +
                (t.transporter_name ? row('Transporter Name', t.transporter_name) : '') +
                row('FO Status',    TV_FO_STATUS[t.fo_status] || t.fo_status || '—') +
                row('Tracking',     TV_LABELS[t.status] || t.status, `color:${statusColor};font-weight:700`) +
                (t.elapsed_hrs != null ? row('Idle Since', `${t.elapsed_hrs} hrs`) : '') +
                (t.today_km > 0        ? row('Today',      `${t.today_km} km`)     : '') +
                (t.address
                    ? `<tr><td colspan="2" style="padding:7px 14px;font-size:11px;color:#6b7280">${t.address}</td></tr>`
                    : '') +
                `</table>` +
                `</div>`;

            marker.addListener('click', () => {
                infoWinRef.current.setContent(content);
                infoWinRef.current.open(mapRef.current, marker);
            });
            newMarkers.push(marker);
        });

        markersRef.current = newMarkers;

        if (clustererRef.current) {
            // Reuse existing clusterer — avoids duplicate map event listeners
            clustererRef.current.addMarkers(newMarkers, false);
        } else {
            clustererRef.current = new MarkerClusterer(mapRef.current, newMarkers, {
                gridSize:           50,
                styles:             TV_CLUSTER_STYLES,
                minimumClusterSize: 2,
            });
            clustererRef.current.setCalculator(TV_CLUSTER_CALC);
        }
    }, [mapReady, statusFilter, trucks]);

    const chipStyle = (key, color) => ({
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '7px 14px', borderRadius: 20, border: 'none',
        cursor: 'pointer', fontSize: 12, fontWeight: 700, flexShrink: 0,
        background: statusFilter === key ? (color || C.accent) : C.chip,
        color:      statusFilter === key ? '#fff' : C.chipText,
        boxShadow:  statusFilter === key ? `0 0 0 2px ${(color || C.accent)}44` : 'none',
        transition: 'all 0.14s',
    });

    return (
        <div style={{ paddingBottom: 16 }}>
            {/* ── Status filter chips + counts ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                {[
                    { key: 'all', label: 'All', color: C.accent, count: trucks.length },
                    ...TV_STATUSES.map(s => ({ key: s, label: TV_LABELS[s], color: TV_COLORS[s], count: counts[s] ?? 0 })),
                ].map(item => (
                    <div key={item.key} style={{ position: 'relative', flexShrink: 0 }}>
                        <button style={chipStyle(item.key, item.color)} onClick={() => setStatusFilter(item.key)}>
                            {item.key !== 'all' && (
                                <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: statusFilter === item.key ? 'rgba(255,255,255,0.6)' : item.color }} />
                            )}
                            {item.label}
                            <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>{item.count}</span>
                            <span
                                onMouseEnter={() => setHoveredTip(item.key)}
                                onMouseLeave={() => setHoveredTip(null)}
                                onClick={e => e.stopPropagation()}
                                style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 1, opacity: 0.75, cursor: 'help' }}
                            >
                                <svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                                    <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
                                </svg>
                            </span>
                        </button>
                        {hoveredTip === item.key && (
                            <div style={{
                                position: 'absolute', top: 'calc(100% + 7px)', left: '50%',
                                transform: 'translateX(-50%)',
                                background: '#1E293B', color: '#fff',
                                fontSize: 11, lineHeight: 1.5,
                                borderRadius: 6, padding: '6px 11px',
                                whiteSpace: 'nowrap', zIndex: 300,
                                pointerEvents: 'none',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.22)',
                            }}>
                                {TV_DESCRIPTIONS[item.key]}
                            </div>
                        )}
                    </div>
                ))}
                <div style={{ flex: 1 }} />
                <button
                    onClick={fetchTrucks}
                    disabled={tvLoading}
                    style={{ padding: '7px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.accent, cursor: tvLoading ? 'default' : 'pointer', fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                    {tvLoading
                        ? <><div style={{ width: 12, height: 12, border: `2px solid ${C.border}`, borderTopColor: C.accent, borderRadius: '50%', animation: 'foi-spin 0.7s linear infinite' }} />Loading…</>
                        : '↻ Refresh'}
                </button>
            </div>

            {/* ── Map ── */}
            <div style={{ borderRadius: 10, overflow: 'hidden', border: `1px solid ${C.border}`, position: 'relative' }}>
                {!mapReady && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: C.textSec, fontSize: 13, background: C.surface, zIndex: 2 }}>
                        <div style={{ width: 16, height: 16, border: `2px solid ${C.border}`, borderTopColor: C.accent, borderRadius: '50%', animation: 'foi-spin 0.7s linear infinite' }} />
                        Loading map…
                    </div>
                )}
                <div ref={mapDivRef} style={{ width: '100%', height: 560 }} />
            </div>

            {/* ── AG-Grid: truck data ── */}
            {visible.length > 0 && (
                <div style={{ marginTop: 20 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: C.text, marginBottom: 8 }}>
                        Truck Data
                        <span style={{ fontWeight: 400, fontSize: 12, color: C.textSec, marginLeft: 8 }}>
                            ({visible.length} truck{visible.length !== 1 ? 's' : ''})
                        </span>
                    </div>
                    <div className="ag-theme-alpine" style={{ height: 380, width: '100%' }}>
                        <AgGridReact
                            rowData={visible}
                            columnDefs={TV_GRID_COL_DEFS}
                            defaultColDef={{ resizable: true, sortable: true, filter: true }}
                            pagination={true}
                            paginationPageSize={15}
                            suppressMovableColumns={true}
                        />
                    </div>
                </div>
            )}

            {!tvLoading && trucks.length === 0 && (
                <div style={{ textAlign: 'center', padding: 48, color: C.textSec, fontSize: 13 }}>
                    No trucks with GPS data found for the selected filters.
                </div>
            )}
        </div>
    );
}

/* ─── Main Component ─────────────────────────────────────────────── */
export default function FOInsightsDashboard() {
    const [activePreset,  setActivePreset]  = useState('l2');
    const [showCustom,    setShowCustom]    = useState(false);
    const customRangeRef = useRef(null);
    const [selectedPlant, setSelectedPlant] = useState('ALL');
    const [plantOptions,  setPlantOptions]  = useState([]);
    const [plantStats,    setPlantStats]    = useState([]);
    const [chartsLoading, setChartsLoading] = useState(false);
    /* custom range — uses selectsRange via single DatePicker */
    const [rangeStart,   setRangeStart]   = useState(null);
    const [rangeEnd,     setRangeEnd]     = useState(null);
    /* active query dates */
    const [startDate, setStartDate] = useState(
        new Date(moment().subtract(1, 'days').startOf('day').toDate())
    );
    const [endDate, setEndDate] = useState(new Date());
    const [loading, setLoading] = useState(false);
    const [data,    setData]    = useState(EMPTY);
    const [alert,   setAlert]   = useState({ show: false, title: '', type: 'default' });
    const [drill,      setDrill]      = useState({ visible: false, title: '', filterType: '', isFc: false, loading: false, data: [] });
    const [fcOverview, setFcOverview] = useState({ loading: false, data: null });
    const [fcDrill,    setFcDrill]    = useState({ visible: false, title: '', data: [], columnDefs: FC_DRILL_COL_DEFS });
    const [foTracking, setFoTracking] = useState({ loading: false, data: [], totalData: null });
    const [selectedZone,      setSelectedZone]      = useState('ALL');
    const [zoneOptions,       setZoneOptions]        = useState([]);
    const [allPlantsWithZone, setAllPlantsWithZone]  = useState([]);
    const [activeTab,  setActiveTab]  = useState('fo-overview');
    const styleRef = useRef(false);

    useEffect(() => {
        if (!styleRef.current) {
            const s = document.createElement('style');
            s.textContent = INJECTED_CSS;
            document.head.appendChild(s);
            styleRef.current = true;
        }
    }, []);

    /* Close the Custom Range calendar when clicking outside it */
    useEffect(() => {
        if (!showCustom) return;
        const handler = (e) => {
            if (customRangeRef.current && !customRangeRef.current.contains(e.target)) {
                setShowCustom(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showCustom]);

    const showAlert = (title, type = 'danger') =>
        setAlert({ show: true, title, type });
    const closeAlert = () =>
        setAlert({ show: false, title: '', type: 'default' });

    const fetchData = async (sd, ed, consignerCode = 'ALL', zoneCodes = null) => {
        setLoading(true);
        setChartsLoading(true);
        try {
            const body = {
                startDate: moment(sd).format('YYYY-MM-DD'),
                endDate:   moment(ed).format('YYYY-MM-DD'),
                consignerCode,
            };
            if (zoneCodes && zoneCodes.length > 0) body.plant_codes = zoneCodes;
            const res = await redirectURL.post('/dashboard/getFOInsightsDashboard', body);
            if (res.data?.status === 'success') {
                setData(res.data.data);
                setPlantStats(res.data.data.plantStats || []);
            } else {
                showAlert('Failed to load dashboard data');
            }
        } catch (err) {
            console.error('FOInsightsDashboard:', err);
            const msg = err?.response?.data?.message || err?.message || 'Error fetching dashboard data';
            showAlert(msg);
        } finally {
            setLoading(false);
            setChartsLoading(false);
        }
    };

    const fetchPlantList = async (sd, ed) => {
        try {
            const res = await redirectURL.post('/dashboard/getFOPlantList', {
                startDate: moment(sd).format('YYYY-MM-DD'),
                endDate:   moment(ed).format('YYYY-MM-DD'),
            });
            if (res.data?.status === 'success') {
                setPlantOptions(res.data.data || []);
            }
        } catch (err) {
            console.error('fetchPlantList:', err);
        }
    };

    const fetchZones = async () => {
        try {
            const res = await redirectURL.post('consignments/allPlantCodes');
            if (res.data?.length > 0) {
                const uniqueZones = [...new Set(
                    res.data.filter(p => p.consigner_zone).map(p => p.consigner_zone)
                )].sort();
                setZoneOptions(uniqueZones.map(z => ({ value: z, label: z })));
                setAllPlantsWithZone(res.data);
            }
        } catch (err) {
            console.error('fetchZones:', err);
        }
    };

    const fetchDrilldown = async (filterType, title, isFc = false) => {
        setDrill({ visible: true, title, filterType, isFc, loading: true, data: [] });
        try {
            const res = await redirectURL.post('/dashboard/getFOInsightsDrilldown', {
                startDate:    moment(startDate).format('YYYY-MM-DD'),
                endDate:      moment(endDate).format('YYYY-MM-DD'),
                filterType,
                consignerCode: selectedPlant,
            });
            if (res.data?.status === 'success') {
                setDrill(prev => ({ ...prev, loading: false, data: res.data.data }));
            } else {
                setDrill(prev => ({ ...prev, loading: false }));
            }
        } catch (err) {
            console.error('Drilldown error:', err);
            setDrill(prev => ({ ...prev, loading: false }));
        }
    };

    const closeDrill = () => setDrill(prev => ({ ...prev, visible: false }));

    const fetchFCOverview = async (sd, ed, plantCode, zoneCodes = null) => {
        setFcOverview({ loading: true, data: null });
        try {
            const params = {
                sdate: moment(sd).format('YYYY-MM-DD'),
                edate: moment(ed).format('YYYY-MM-DD'),
            };
            if (plantCode && plantCode !== 'ALL') {
                params.plant_codes = [plantCode];
            } else if (zoneCodes && zoneCodes.length > 0) {
                params.plant_codes = zoneCodes;
            }
            const res = await redirectURL.post('/consignments/forceClosureConsignmentsData', params);
            const raw = res.data || {};
            const foLevel  = raw.fo_level_data       || {};
            const dlvLevel = raw.delivery_level_data  || {};
            const normLabel = s => (s || '').replace(/\bsim\b/gi, 'SIM');

            const totalFoCount       = foLevel.total_data?.count  || 0;
            const totalFoData        = foLevel.total_data?.data   || [];
            const totalDeliveryCount = dlvLevel.total_data?.count || 0;
            const totalDeliveryData  = dlvLevel.total_data?.data  || [];

            const primaryFo = [], primaryDlv = [], secondaryFo = [], secondaryDlv = [];

            Object.entries(foLevel).forEach(([pk, pv]) => {
                if (pk === 'total_data') return;
                let cnt = 0, dat = [];
                Object.entries(pv).forEach(([sk, sv]) => {
                    const c = sv.count || 0;
                    cnt += c;
                    dat = dat.concat(sv.data || []);
                    if (c > 0) secondaryFo.push({ label: normLabel(sk), count: c, data: sv.data || [] });
                });
                if (cnt > 0) primaryFo.push({ label: normLabel(pk), count: cnt, data: dat });
            });

            Object.entries(dlvLevel).forEach(([pk, pv]) => {
                if (pk === 'total_data') return;
                let cnt = 0, dat = [];
                Object.entries(pv).forEach(([sk, sv]) => {
                    const c = sv.count || 0;
                    cnt += c;
                    dat = dat.concat(sv.data || []);
                    if (c > 0) secondaryDlv.push({ label: normLabel(sk), count: c, data: sv.data || [] });
                });
                if (cnt > 0) primaryDlv.push({ label: normLabel(pk), count: cnt, data: dat });
            });

            setFcOverview({
                loading: false,
                data: {
                    totalFoCount, totalFoData,
                    totalDeliveryCount, totalDeliveryData,
                    primaryFo, primaryDlv, secondaryFo, secondaryDlv,
                },
            });
        } catch (err) {
            console.error('FCOverview:', err);
            setFcOverview({ loading: false, data: null });
        }
    };

    const fetchFOTracking = async (sd, ed, plantCode, zoneCodes = null) => {
        setFoTracking(prev => ({ ...prev, loading: true }));
        try {
            const query = {
                sdate: moment(sd).format('YYYY-MM-DD'),
                edate: moment(ed).format('YYYY-MM-DD'),
            };
            if (plantCode && plantCode !== 'ALL') {
                query.plant_codes = [plantCode];
            } else if (zoneCodes && zoneCodes.length > 0) {
                query.plant_codes = zoneCodes;
            }
            const res = await redirectURL.post('consignments/getConsignmentTrackingReport', query);
            if (res.data?.status === 'success') {
                setFoTracking({
                    loading: false,
                    data: res.data.consignment_tracking_report || [],
                    totalData: res.data.total_data || null,
                });
            } else {
                setFoTracking({ loading: false, data: [], totalData: null });
            }
        } catch (err) {
            console.error('FOTracking:', err);
            setFoTracking({ loading: false, data: [], totalData: null });
        }
    };

    useEffect(() => {
        fetchData(startDate, endDate, 'ALL');
        fetchPlantList(startDate, endDate);
        fetchFCOverview(startDate, endDate, 'ALL');
        fetchFOTracking(startDate, endDate, 'ALL');
        fetchZones();
        // eslint-disable-next-line
    }, []);

    const applyPreset = (p) => {
        setActivePreset(p.id);
        setShowCustom(false);
        setRangeStart(null);
        setRangeEnd(null);
        setSelectedPlant('ALL');
        setSelectedZone('ALL');
        const sd = new Date(moment().subtract(p.days - 1, 'days').startOf('day').toDate());
        const ed = new Date();
        setStartDate(sd);
        setEndDate(ed);
        fetchData(sd, ed, 'ALL');
        fetchPlantList(sd, ed);
        fetchFCOverview(sd, ed, 'ALL');
        fetchFOTracking(sd, ed, 'ALL');
    };

    const getZoneCodes = (zone) => {
        if (!zone || zone === 'ALL') return null;
        const codes = allPlantsWithZone
            .filter(p => p.consigner_zone === zone && p.consigner_code)
            .map(p => p.consigner_code);
        return codes.length > 0 ? codes : null;
    };

    const handleZoneChange = (zone) => {
        setSelectedZone(zone);
        setSelectedPlant('ALL');
        if (zone === 'ALL') {
            fetchPlantList(startDate, endDate);
            fetchData(startDate, endDate, 'ALL');
            fetchFCOverview(startDate, endDate, 'ALL');
            fetchFOTracking(startDate, endDate, 'ALL');
        } else {
            const zonePlants = allPlantsWithZone
                .filter(p => p.consigner_zone === zone && p.consigner_code)
                .map(p => ({ code: p.consigner_code, name: p.consigner_name }));
            setPlantOptions(zonePlants);
            const codes = zonePlants.map(p => p.code);
            fetchData(startDate, endDate, 'ALL', codes);
            fetchFCOverview(startDate, endDate, 'ALL', codes);
            fetchFOTracking(startDate, endDate, 'ALL', codes);
        }
    };

    const handlePlantChange = (code) => {
        setSelectedPlant(code);
        const zoneCodes = code === 'ALL' ? getZoneCodes(selectedZone) : null;
        fetchData(startDate, endDate, code, zoneCodes);
        fetchFCOverview(startDate, endDate, code, zoneCodes);
        fetchFOTracking(startDate, endDate, code, zoneCodes);
    };

    /* react-datepicker selectsRange callback */
    const handleRangeChange = (update) => {
        const [start, end] = update;
        setRangeStart(start);
        setRangeEnd(end);
        if (start && end) {
            setSelectedPlant('ALL');
            setSelectedZone('ALL');
            setStartDate(start);
            setEndDate(end);
            fetchData(start, end, 'ALL');
            fetchPlantList(start, end);
            fetchFCOverview(start, end, 'ALL');
            fetchFOTracking(start, end, 'ALL');
            setShowCustom(false);
        }
    };

    const { section1: s1, section2: s2, section3: s3 } = data;

    return (
        <>
        <div className="col-xl-12 col-lg-12">
            <SweetAlert
                show={alert.show}
                title={<span style={{ fontSize: '1rem' }}>{alert.title}</span>}
                type={alert.type}
                onConfirm={closeAlert}
                confirmBtnBsStyle={alert.type}
            />

            <h5 className="col-xl-12 col-lg-12">
                <i className="icofont icofont-dashboard-web cus-i"></i>
                <span style={{ fontWeight: '600', marginLeft: 8 }}>FO Analytics</span>
            </h5>

            <div className="col-xl-12 col-lg-12 tab- pt-20px ml-18p whiteRow">

            {/* ══ PAGE BODY ════════════════════════════════════════════ */}
            <div style={{ padding: '14px 20px 36px' }}>

                    {/* ── Global filter bar — shared across all tabs ── */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 0 14px',
                        borderBottom: `1px solid ${C.border}`,
                        marginBottom: 16,
                        flexWrap: 'wrap',
                    }}>
                        {/* Zone filter */}
                        <span style={{
                            fontSize: 11, fontWeight: 700, color: C.textSec,
                            flexShrink: 0, letterSpacing: 0.3,
                        }}>Filter by Plant Zone</span>
                        <div style={{ width: 200, flexShrink: 0 }}>
                            <Select
                                options={[
                                    { value: 'ALL', label: 'All Zones' },
                                    ...zoneOptions,
                                ]}
                                value={selectedZone === 'ALL'
                                    ? { value: 'ALL', label: 'All Zones' }
                                    : { value: selectedZone, label: selectedZone }
                                }
                                onChange={opt => handleZoneChange(opt.value)}
                                isSearchable
                                placeholder="Select zone…"
                                menuPortalTarget={document.body}
                                styles={{
                                    menuPortal: b => ({ ...b, zIndex: 9999 }),
                                    control: b => ({ ...b, fontSize: 12, minHeight: 34, borderColor: C.border, borderRadius: 6, boxShadow: 'none', cursor: 'pointer' }),
                                    option: (b, s) => ({ ...b, fontSize: 11, cursor: 'pointer', background: s.isSelected ? C.accent : s.isFocused ? '#EEF2FF' : '#fff', color: s.isSelected ? '#fff' : C.textPri }),
                                    singleValue: b => ({ ...b, fontSize: 11, color: C.textPri }),
                                    placeholder: b => ({ ...b, fontSize: 11, color: C.textMut }),
                                }}
                            />
                        </div>

                        {/* Divider */}
                        <div style={{ width: 1, height: 28, background: C.border, flexShrink: 0 }} />

                        {/* Plant filter */}
                        <span style={{
                            fontSize: 11, fontWeight: 700, color: C.textSec,
                            flexShrink: 0, letterSpacing: 0.3,
                        }}>Filter by Plant</span>
                        <div style={{ width: 300, flexShrink: 0 }}>
                            <Select
                                options={[
                                    { value: 'ALL', label: selectedZone === 'ALL' ? 'All Plants' : `All Plants in ${selectedZone}` },
                                    ...plantOptions.map(p => ({
                                        value: p.code,
                                        label: `${p.name} (${p.code})`,
                                    })),
                                ]}
                                value={selectedPlant === 'ALL'
                                    ? { value: 'ALL', label: selectedZone === 'ALL' ? 'All Plants' : `All Plants in ${selectedZone}` }
                                    : plantOptions.find(p => p.code === selectedPlant)
                                        ? { value: selectedPlant, label: `${plantOptions.find(p => p.code === selectedPlant).name} (${selectedPlant})` }
                                        : { value: selectedPlant, label: selectedPlant }
                                }
                                onChange={opt => handlePlantChange(opt.value)}
                                isSearchable
                                placeholder="Search plant…"
                                menuPortalTarget={document.body}
                                styles={{
                                    menuPortal: b => ({ ...b, zIndex: 9999 }),
                                    control: b => ({ ...b, fontSize: 12, minHeight: 34, borderColor: C.border, borderRadius: 6, boxShadow: 'none', cursor: 'pointer' }),
                                    option: (b, s) => ({ ...b, fontSize: 11, cursor: 'pointer', background: s.isSelected ? C.accent : s.isFocused ? '#EEF2FF' : '#fff', color: s.isSelected ? '#fff' : C.textPri }),
                                    singleValue: b => ({ ...b, fontSize: 11, color: C.textPri }),
                                    placeholder: b => ({ ...b, fontSize: 11, color: C.textMut }),
                                }}
                            />
                        </div>
                        {(selectedZone !== 'ALL' || selectedPlant !== 'ALL') && (
                            <button
                                onClick={() => {
                                    setSelectedZone('ALL');
                                    setSelectedPlant('ALL');
                                    fetchPlantList(startDate, endDate);
                                    fetchData(startDate, endDate, 'ALL');
                                    fetchFCOverview(startDate, endDate, 'ALL');
                                    fetchFOTracking(startDate, endDate, 'ALL');
                                }}
                                style={{
                                    padding: '5px 14px', fontSize: 11, fontWeight: 700,
                                    border: `1px solid ${C.border}`, borderRadius: 5,
                                    background: C.surface, color: C.textSec,
                                    cursor: 'pointer', flexShrink: 0,
                                }}
                            >Reset</button>
                        )}
                        {/* Spacer */}
                        <div style={{ flex: 1 }} />

                        {/* Preset chips */}
                        {PRESETS.map(p => (
                            <button
                                key={p.id}
                                className="foi-chip"
                                onClick={() => applyPreset(p)}
                                disabled={loading}
                                style={{
                                    padding: '5px 12px',
                                    borderRadius: 20, border: 'none',
                                    cursor: loading ? 'default' : 'pointer',
                                    fontSize: 11, fontWeight: 700, letterSpacing: 0.2,
                                    background: activePreset === p.id ? C.chipActive : C.chip,
                                    color: activePreset === p.id ? '#FFFFFF' : C.chipText,
                                    boxShadow: activePreset === p.id ? `0 0 0 2px ${C.chipActive}33` : 'none',
                                    flexShrink: 0,
                                }}
                            >{p.label}</button>
                        ))}

                        {/* Custom Range */}
                        <div ref={customRangeRef} style={{ position: 'relative', flexShrink: 0 }}>
                            <button
                                className="foi-chip"
                                onClick={() => { setShowCustom(v => !v); setActivePreset('cr'); }}
                                disabled={loading}
                                style={{
                                    padding: '5px 12px',
                                    borderRadius: 20, border: 'none',
                                    cursor: loading ? 'default' : 'pointer',
                                    fontSize: 11, fontWeight: 700, letterSpacing: 0.2,
                                    background: activePreset === 'cr' ? C.chipActive : C.chip,
                                    color: activePreset === 'cr' ? '#FFFFFF' : C.chipText,
                                    display: 'flex', alignItems: 'center', gap: 5,
                                }}
                            >
                                <svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M11 6.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1z"/>
                                    <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/>
                                </svg>
                                Custom Range
                            </button>
                            {showCustom && (
                                <div style={{
                                    position: 'absolute', top: 'calc(100% + 10px)', right: 0,
                                    zIndex: 500, background: C.surface, borderRadius: 10,
                                    boxShadow: '0 8px 32px rgba(15,29,53,0.18)',
                                    border: `1px solid ${C.border}`, padding: 4,
                                }}>
                                    <DatePicker
                                        selectsRange startDate={rangeStart} endDate={rangeEnd}
                                        onChange={handleRangeChange} inline
                                        maxDate={new Date()} monthsShown={2}
                                        calendarClassName="foi-cal"
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                        yearDropdownItemNumber={10}
                                    />
                                    <div style={{
                                        padding: '8px 12px', borderTop: `1px solid ${C.border}`,
                                        display: 'flex', alignItems: 'center',
                                        justifyContent: 'space-between',
                                        fontSize: 10, color: C.textMut,
                                    }}>
                                        <span>
                                            {rangeStart
                                                ? `${moment(rangeStart).format('DD MMM')} → ${rangeEnd ? moment(rangeEnd).format('DD MMM YYYY') : '…'}`
                                                : 'Click a start date'}
                                        </span>
                                        <button onClick={() => setShowCustom(false)} style={{
                                            background: 'none', border: 'none',
                                            color: C.textSec, fontSize: 11,
                                            cursor: 'pointer', fontWeight: 600,
                                        }}>Close</button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Loading spinner */}
                        {loading && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.textSec, fontSize: 11, flexShrink: 0 }}>
                                <div style={{
                                    width: 13, height: 13,
                                    border: '2px solid rgba(30,41,59,0.12)',
                                    borderTopColor: C.chipActive, borderRadius: '50%',
                                    animation: 'foi-spin 0.7s linear infinite',
                                }} />
                                Fetching…
                            </div>
                        )}
                    </div>

                    {/* ── Tab Bar ── */}
                    <div style={{
                        display: 'flex',
                        borderBottom: `2px solid ${C.border}`,
                        marginBottom: 20,
                    }}>
                        {[
                            { id: 'fo-overview',       label: 'Summary' },
                            { id: 'fo-tracking',       label: 'Detail Report' },
                            { id: 'truck-visibility',  label: 'Truck Visibility' },
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                style={{
                                    padding: '10px 24px',
                                    border: 'none',
                                    background: 'none',
                                    cursor: 'pointer',
                                    fontSize: 13,
                                    fontWeight: activeTab === tab.id ? 700 : 500,
                                    color: activeTab === tab.id ? C.accent : C.textSec,
                                    borderBottom: activeTab === tab.id
                                        ? `2px solid ${C.accent}` : '2px solid transparent',
                                    marginBottom: -2,
                                    letterSpacing: 0.2,
                                    transition: 'color 0.14s, border-color 0.14s',
                                    outline: 'none',
                                }}
                            >{tab.label}</button>
                        ))}
                    </div>

                    {activeTab === 'fo-overview' && (
                    <>
                    {/* ════ SECTIONS 1 & 2 — SIDE BY SIDE ════════════════ */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
                        gap: 16,
                        marginBottom: 16,
                    }}>
                        {/* Section 1 — FO Status */}
                        <SectionCard
                            title="FO Status Distribution"
                            accentColor={C.accent}
                            loading={loading}
                            style={{ marginBottom: 0 }}
                        >
                            <StatStrip items={[
                                {
                                    value: s1.total,
                                    numColor: C.numColor,
                                    label: 'Total FOs',
                                    tooltip: 'All FOs created in the selected date range',
                                    onClick: () => fetchDrilldown('total', 'Total FOs'),
                                },
                                {
                                    value: s1.active,
                                    numColor: C.accent,
                                    badge: safePct(s1.active, s1.total),
                                    label: 'Active',
                                    sub: 'Inside Plant · In Transit · Near Customer',
                                    tooltip: 'Inside Plant · In Transit · Near Customer',
                                    onClick: () => fetchDrilldown('active', 'Active FOs'),
                                },
                                {
                                    value: s1.delivered,
                                    numColor: C.green,
                                    badge: safePct(s1.delivered, s1.total),
                                    label: 'Delivered',
                                    sub: 'Reached Customer · Left Customer',
                                    tooltip: 'Reached Customer · Left Customer',
                                    onClick: () => fetchDrilldown('delivered', 'Delivered FOs'),
                                },
                                {
                                    value: s1.forceClosed,
                                    numColor: C.orange,
                                    badge: safePct(s1.forceClosed, s1.total),
                                    label: 'Force Closed',
                                    tooltip: 'Force Closed FOs',
                                    onClick: () => fetchDrilldown('forceClosed', 'Force Closed FOs', true),
                                },
                                {
                                    value: s1.cancelled,
                                    numColor: C.textMut,
                                    badge: safePct(s1.cancelled, s1.total),
                                    label: 'Cancelled',
                                    tooltip: 'Cancelled FOs',
                                    onClick: () => fetchDrilldown('cancelled', 'Cancelled FOs'),
                                },
                            ]} />
                        </SectionCard>

                        {/* Section 2 — SIM Consent */}
                        <SectionCard
                            title="SIM Consent Status"
                            accentColor={C.green}
                            loading={loading}
                            style={{ marginBottom: 0 }}
                        >
                            <StatStrip items={[
                                {
                                    value: s2.total,
                                    numColor: C.numColor,
                                    label: 'Total FOs',
                                    tooltip: 'All FOs in the selected date range',
                                    onClick: () => fetchDrilldown('total', 'Total FOs'),
                                },
                                {
                                    value: s2.approved,
                                    numColor: C.green,
                                    badge: safePct(s2.approved, s2.total),
                                    label: 'Approved',
                                    sub: 'Consent Approved · Approved',
                                    tooltip: 'SIM consent status: Approved or Consent Approved',
                                    onClick: () => fetchDrilldown('simApproved', 'Consent Approved FOs'),
                                },
                                {
                                    value: s2.pending,
                                    numColor: C.orange,
                                    badge: safePct(s2.pending, s2.total),
                                    label: 'Pending',
                                    sub: 'Consent Pending · Not Registered',
                                    tooltip: 'SIM consent status: Consent Pending or Not Registered',
                                    onClick: () => fetchDrilldown('simPending', 'Consent Pending FOs'),
                                },
                                {
                                    value: safePct(s2.approved, s2.total),
                                    numColor: C.accent,
                                    label: 'Approval Rate',
                                    sub: 'Approved ÷ Total FOs',
                                    isPct: true,
                                    featured: true,
                                    tooltip: '(Approved FOs / Total FOs) × 100',
                                    onClick: () => fetchDrilldown('simApproved', 'Consent Approved FOs'),
                                },
                            ]} />
                        </SectionCard>
                    </div>

                    {/* Section 3 — Tracking Mode */}
                    <SectionCard
                        title="Tracking Mode"
                        accentColor={C.purple}
                        loading={loading}
                    >
                        <StatStrip items={[
                            {
                                value: s3.total,
                                numColor: C.numColor,
                                label: 'Total FOs',
                                tooltip: 'All FOs in the selected date range',
                                onClick: () => fetchDrilldown('total', 'Total FOs'),
                            },
                            {
                                value: s3.gps,
                                numColor: C.accent,
                                badge: safePct(s3.gps, s3.total),
                                label: 'GPS Tracked',
                                sub: 'tracking_mode = GPS',
                                tooltip: 'FOs tracked via GPS',
                                onClick: () => fetchDrilldown('gpsMode', 'GPS Tracked FOs'),
                            },
                            {
                                value: s3.sim,
                                numColor: C.green,
                                badge: safePct(s3.sim, s3.total),
                                label: 'SIM Tracked',
                                sub: 'tracking_mode = SIM',
                                tooltip: 'FOs tracked via SIM',
                                onClick: () => fetchDrilldown('simMode', 'SIM Tracked FOs'),
                            },
                            {
                                value: s3.invalid,
                                numColor: C.orange,
                                badge: safePct(s3.invalid, s3.total),
                                label: 'Invalid Numbers',
                                sub: 'No valid tracking mode',
                                tooltip: 'FOs with no GPS or SIM tracking mode (invalid driver number)',
                                onClick: () => fetchDrilldown('invalidMode', 'Invalid Number FOs'),
                            },
                            {
                                value: s3.bsnl ?? 0,
                                numColor: C.purple,
                                badge: safePct(s3.bsnl ?? 0, s3.total ?? 0),
                                label: 'BSNL Number',
                                sub: 'prob_service_provider = BSNL',
                                tooltip: 'FOs where the SIM service provider is BSNL',
                                onClick: () => fetchDrilldown('bsnlMode', 'BSNL Number FOs'),
                            },
                        ]} />
                    </SectionCard>

                    {/* Plant performance charts — only when showing all plants */}
                    {selectedPlant === 'ALL' && (
                        <PlantCharts
                            plantStats={plantStats}
                            loading={chartsLoading}
                        />
                    )}

                    {/* Force Closure Overview — always shown, respects plant filter */}
                    <FCOverviewSection
                        data={fcOverview.data}
                        loading={fcOverview.loading}
                        onSliceClick={(title, rows, colDefs) => setFcDrill({ visible: true, title, data: rows, columnDefs: colDefs || FC_DRILL_COL_DEFS })}
                    />
                    </>
                    )}

                    {activeTab === 'fo-tracking' && (
                        <FOTrackingSection
                            data={foTracking.data}
                            loading={foTracking.loading}
                            totalData={foTracking.totalData}
                        />
                    )}

                    {activeTab === 'truck-visibility' && (
                        <TruckVisibilitySection
                            startDate={startDate}
                            endDate={endDate}
                            selectedPlant={selectedPlant}
                            selectedZone={selectedZone}
                        />
                    )}

            </div>
            </div>{/* end whiteRow */}
        </div>

        <DrilldownPanel
            visible={drill.visible}
            title={drill.title}
            loading={drill.loading}
            data={drill.data}
            isFcFilter={drill.isFc}
            onClose={closeDrill}
        />
        <DrilldownPanel
            visible={fcDrill.visible}
            title={fcDrill.title}
            loading={false}
            data={fcDrill.data}
            columnDefs={fcDrill.columnDefs}
            onClose={() => setFcDrill(prev => ({ ...prev, visible: false }))}
        />
        </>
    );
}
